// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ScrollBox.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import elementResizeDetector from 'share/utils/Izenda.SizeDetector';
import raf from 'raf';
import TWEEN from 'tween.js';
import classnames from 'classnames';
import style from 'dom-helpers/style';
import fastdom from 'fastdom';
import { isNil, debounce, omit, extend, some } from 'lodash/fp';
const NUMBER_ZERO = 0;
const NUMBER_TWO = 2;
const NUMBER_DEBOUNCE = 250;
const NUMBER_DETECT_SCROLLING = 100;
const NUMBER_TWEEN = 400;

const eventOptions = {
  capture: false,
  passive: true
};
let scrollbarWidth;

const TRACK_MIN_SIZE = 20;
const HORIZONTAL_TRACK_HEIGHT = 10;
const TRACK_PADDING = 2;

export default class ScrollBox extends Component {
  static propTypes = {
    onScroll: PropTypes.func,
    fullSize: PropTypes.bool,
    onScrollStart: PropTypes.func,
    onScrollStop: PropTypes.func,
    autoHide: PropTypes.bool,
    autoHideTimeout: PropTypes.number,
    showTrackTimeout: PropTypes.number,
    autoHeight: PropTypes.bool,
    disabled: PropTypes.bool,
    autoHeightMin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    autoHeightMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showWhenHover: PropTypes.bool,
    shouldInjectScrollCallbackToChild: PropTypes.bool,
    viewClassName: PropTypes.string,
    fullHeight: PropTypes.bool,
    fullWidth: PropTypes.bool,
    alwaysShowHorizontalScroll: PropTypes.bool,
    alwaysShowVerticalScroll: PropTypes.bool,
    onUpdate: PropTypes.func,
    stickyScrollBottom: PropTypes.number,
    triggerOnScrollWhenScrollTo: PropTypes.bool
  };

  static defaultProps = {
    fullSize: false,
    autoHide: true,
    autoHideTimeout: 200,
    showTrackTimeout: 200,
    autoHeight: false,
    disabled: false,
    autoHeightMin: 0,
    autoHeightMax: 200,
    showWhenHover: true,
    shouldInjectScrollCallbackToChild: false,
    fullHeight: false,
    fullWidth: false,
    alwaysShowHorizontalScroll: false,
    alwaysShowVerticalScroll: false,
    triggerOnScrollWhenScrollTo: true
  };

  componentDidMount() {
    this.addListeners();
    this.update();
  }

  componentDidUpdate() {
    this.props.onUpdate && this.props.onUpdate();
    this.update();
  }

  componentWillUnmount() {
    this.removeListeners();
    fastdom.clear(this.fastdomMeasure);
    fastdom.clear(this.fastdomMutate);
    this.hideTracksTimeout && clearTimeout(this.hideTracksTimeout);
    this.mouseEnterTimeout && clearTimeout(this.mouseEnterTimeout);
    this.mouseLeaveTimeout && clearTimeout(this.mouseLeaveTimeout);
    !isNil(this.detectScrollingInterval) && clearInterval(this.detectScrollingInterval);
  }

  /**
   * Get scroll left possition
   */
  getScrollLeft() {
    return this.refs.view && this.refs.view.scrollLeft;
  }

  /**
   * Get scroll top px
   */
  getScrollTop() {
    return this.refs.view && this.refs.view.scrollTop;
  }

  /**
   * Get scroll width px
   */
  getScrollWidth() {
    return this.refs.view && this.refs.view.scrollWidth;
  }

  /**
   * Get scroll height px
   */
  getScrollHeight() {
    return this.refs.view && this.refs.view.scrollHeight;
  }

  /**
   * Get the client viewport width
   */
  getClientWidth() {
    return this.refs.view && this.refs.view.clientWidth;
  }

  /**
   * Get the client viewport height
   */
  getClientHeight() {
    return this.refs.view && this.refs.view.clientHeight;
  }

  /**
   * Get the view possition and dimmension
   */
  getValues() {
    const view = this.refs.view;
    return view
      ? {
          left: view.scrollLeft / (view.scrollWidth - view.clientWidth) || NUMBER_ZERO,
          top: view.scrollTop / (view.scrollHeight - view.clientHeight) || NUMBER_ZERO,
          scrollLeft: view.scrollLeft,
          scrollTop: view.scrollTop,
          scrollWidth: view.scrollWidth,
          scrollHeight: view.scrollHeight,
          clientWidth: view.clientWidth,
          clientHeight: view.clientHeight
        }
      : undefined;
  }

  /**
   * Get horizontal thumb width
   */
  getThumbHorizontalWidth() {
    const view = this.refs.view;
    const trackWidth = getInnerWidth(this.refs.trackHorizontal);
    const width = view.clientWidth / view.scrollWidth * trackWidth;
    return trackWidth === width ? NUMBER_ZERO : Math.max(width, TRACK_MIN_SIZE);
  }

  /**
   * Get vertical thumb width
   */
  getThumbVerticalHeight() {
    const view = this.refs.view;
    const trackHeight = getInnerHeight(this.refs.trackVertical);
    const height = view.clientHeight / view.scrollHeight * trackHeight;
    return trackHeight === height ? NUMBER_ZERO : Math.max(height, TRACK_MIN_SIZE);
  }

  getScrollLeftForOffset(offset) {
    const view = this.refs.view;
    const trackWidth = getInnerWidth(this.refs.trackHorizontal);
    const thumbWidth = this.getThumbHorizontalWidth();
    return offset / (trackWidth - thumbWidth) * (view.scrollWidth - view.clientWidth);
  }

  getScrollTopForOffset(offset) {
    const view = this.refs.view;
    const trackHeight = getInnerHeight(this.refs.trackVertical);
    const thumbHeight = this.getThumbVerticalHeight();
    return offset / (trackHeight - thumbHeight) * (view.scrollHeight - view.clientHeight);
  }

  scrollVertical(position, withoutAnimate) {
    if (this.refs.view) {
      const view = this.refs.view;

      const start = { scrollTop: view.scrollTop };
      const end = { scrollTop: position };
      const onUpdate = () => {
        view.scrollTop = position;
      };
      if (withoutAnimate) {
        onUpdate();
      } else {
        tween(start, end, onUpdate);
      }
    }
  }

  isInsideOfViewport() {
    if (this.refs.view && this.props.stickyScrollBottom) {
      const top = this.refs.view.getBoundingClientRect().top;
      return top + this.props.stickyScrollBottom + HORIZONTAL_TRACK_HEIGHT <= window.innerHeight;
    }
    return false;
  }

  stickHorizontalScroll() {
    if (this.refs.trackHorizontal && this.refs.view) {
      const width = this.getClientWidth();
      const left = this.refs.view.getBoundingClientRect().left;
      const trackHorizontalStyle = this.refs.trackHorizontal.style;
      fastdom.mutate(() => {
        trackHorizontalStyle.position = 'fixed';
        trackHorizontalStyle.bottom = `${this.props.stickyScrollBottom + TRACK_PADDING}px`;
        trackHorizontalStyle.left = `${left + TRACK_PADDING}px`;
        trackHorizontalStyle.width = `${width - (HORIZONTAL_TRACK_HEIGHT + TRACK_PADDING)}px`;
      });
    }
  }

  unstickHorizontalScroll() {
    if (this.refs.trackHorizontal) {
      const trackHorizontalStyle = this.refs.trackHorizontal.style;
      fastdom.mutate(() => {
        trackHorizontalStyle.position = '';
        trackHorizontalStyle.bottom = '';
        trackHorizontalStyle.left = '';
        trackHorizontalStyle.width = '';
      });
    }
  }

  getTrackHeight() {
    return HORIZONTAL_TRACK_HEIGHT;
  }

  scrollHorizontal(position) {
    if (this.refs.view) {
      const view = this.refs.view;
      const start = { scrollLeft: view.scrollLeft };
      const end = { scrollLeft: position };
      const onUpdate = () => {
        view.scrollLeft = position;
      };
      tween(start, end, onUpdate);
    }
  }

  scrollTo(position) {
    if (this.refs.view) {
      const view = this.refs.view;
      const start = { scrollLeft: view.scrollLeft, scrollTop: view.scrollTop };
      const end = { scrollLeft: position.left, scrollTop: position.top };

      const changeTriggerOnScrollEventValue = () => {
        this.triggerOnScrollEvent = this.props.triggerOnScrollWhenScrollTo;
      };

      const onUpdate = function() {
        view.scrollLeft = this.scrollLeft;
        view.scrollTop = this.scrollTop;
        changeTriggerOnScrollEventValue();
      };
      tween(start, end, onUpdate);
    }
  }

  addListeners() {
    const scrollbox = this;
    const view = this.refs.view;
    const trackHorizontal = this.refs.trackHorizontal;
    const trackVertical = this.refs.trackVertical;
    const thumbHorizontal = this.refs.thumbHorizontal;
    const thumbVertical = this.refs.thumbVertical;

    if (typeof document === 'undefined') {
      return;
    }

    view.addEventListener('scroll', this.handleScroll, eventOptions);

    // Show tracks when hover
    if (this.props.showWhenHover) {
      view.addEventListener('mouseover', this.handelViewMouseEnter, eventOptions);
      view.addEventListener('mouseleave', this.handelViewMouseLeave, eventOptions);
    }

    if (!getScrollbarWidth()) return;
    trackHorizontal.addEventListener('mouseover', this.handleTrackMouseEnter, eventOptions);
    trackHorizontal.addEventListener('mouseleave', this.handleTrackMouseLeave, eventOptions);
    trackHorizontal.addEventListener('mousedown', this.handleHorizontalTrackMouseDown, eventOptions);
    trackVertical.addEventListener('mouseover', this.handleTrackMouseEnter, eventOptions);
    trackVertical.addEventListener('mouseleave', this.handleTrackMouseLeave, eventOptions);
    trackVertical.addEventListener('mousedown', this.handleVerticalTrackMouseDown, eventOptions);
    thumbHorizontal.addEventListener('mousedown', this.handleHorizontalThumbMouseDown, eventOptions);
    thumbVertical.addEventListener('mousedown', this.handleVerticalThumbMouseDown, eventOptions);

    this.debouncedResize = debounce(NUMBER_DEBOUNCE, () => {
      scrollbox.handleWindowResize();
    });

    elementResizeDetector.listenTo(view, this.debouncedResize);
  }

  removeListeners() {
    const view = this.refs.view;
    const trackHorizontal = this.refs.trackHorizontal;
    const trackVertical = this.refs.trackVertical;
    const thumbHorizontal = this.refs.thumbHorizontal;
    const thumbVertical = this.refs.thumbVertical;

    if (typeof document === 'undefined') {
      return;
    }

    view.removeEventListener('scroll', this.handleScroll, eventOptions);

    if (!getScrollbarWidth()) {
      return;
    }

    if (this.props.showWhenHover) {
      view.removeEventListener('mouseover', this.handelViewMouseEnter, eventOptions);
      view.removeEventListener('mouseleave', this.handelViewMouseLeave, eventOptions);
    }

    trackHorizontal.removeEventListener('mouseover', this.handleTrackMouseEnter, eventOptions);
    trackHorizontal.removeEventListener('mouseleave', this.handleTrackMouseLeave, eventOptions);
    trackHorizontal.removeEventListener('mousedown', this.handleHorizontalTrackMouseDown, eventOptions);
    trackVertical.removeEventListener('mouseover', this.handleTrackMouseEnter, eventOptions);
    trackVertical.removeEventListener('mouseleave', this.handleTrackMouseLeave, eventOptions);
    trackVertical.removeEventListener('mousedown', this.handleVerticalTrackMouseDown, eventOptions);
    thumbHorizontal.removeEventListener('mousedown', this.handleHorizontalThumbMouseDown, eventOptions);
    thumbVertical.removeEventListener('mousedown', this.handleVerticalThumbMouseDown, eventOptions);
    elementResizeDetector.removeAllListeners(view);

    this.teardownDragging();
  }

  handleScroll = event => {
    if (this.props.isDisabled || this.props.fullSize) {
      return;
    }
    this.triggerOnScrollEvent && this.props.onScroll && this.props.onScroll(event);
    this.triggerOnScrollEvent = true;

    this.update(updateViewScrollPosition.bind(this));
    this.detectScrolling();

    function updateViewScrollPosition(values) {
      this.viewScrollLeft = values.scrollLeft;
      this.viewScrollTop = values.scrollTop;
    }
  };
  /**
   * Check if the mouseover target is in child scrollbox of View
   * Only show the child scroll , hide the parent scroll
   */
  targetInChildScrollbox(event) {
    const view = this.refs.view;
    const childScrollboxes = $izenda(view).find('.izenda-ScrollBox');

    return !!some(scrollbox => $izenda.contains(scrollbox, event.target), childScrollboxes);
  }

  handelViewMouseEnter = event => {
    if (this.targetInChildScrollbox(event)) {
      this.handelViewMouseLeave();
      return;
    }

    this.mouseEnterTimeout && clearTimeout(this.mouseEnterTimeout);

    this.mouseEnterTimeout = setTimeout(() => {
      this.update();
      this.handleScrollStartAutoHide();
    }, this.props.showTrackTimeout);
  };

  handelViewMouseLeave = () => {
    this.mouseLeaveTimeout && clearTimeout(this.mouseLeaveTimeout);
    this.mouseLeaveTimeout = setTimeout(() => {
      this.handleScrollStopAutoHide();
    }, this.props.showTrackTimeout);
  };

  handleScrollStart() {
    if (this.props.fullSize) return;

    this.props.onScrollStart && this.props.onScrollStart();
    this.handleScrollStartAutoHide();
  }

  handleScrollStartAutoHide() {
    if (this.props.fullSize || !this.props.autoHide) return;

    this.showTracks();
  }

  handleScrollStop() {
    if (this.props.fullSize) return;

    this.props.onScrollStop && this.props.onScrollStop();
    this.handleScrollStopAutoHide();
  }

  handleScrollStopAutoHide() {
    if (this.props.fullSize || !this.props.autoHide) return;

    this.hideTracks();
  }

  handleWindowResize() {
    this.update();
  }

  handleHorizontalTrackMouseDown = event => {
    if (this.props.fullSize) return;

    const view = this.refs.view;
    const target = event.target;
    const clientX = event.clientX;
    const targetLeft = target.getBoundingClientRect().left;
    const thumbWidth = this.getThumbHorizontalWidth();
    const offset = Math.abs(targetLeft - clientX) - thumbWidth / NUMBER_TWO;

    view.scrollLeft = this.getScrollLeftForOffset(offset);
  };

  handleVerticalTrackMouseDown = event => {
    if (this.props.fullSize) return;

    const view = this.refs.view;
    const target = event.target;
    const clientY = event.clientY;
    const targetTop = target.getBoundingClientRect().top;
    const thumbHeight = this.getThumbVerticalHeight();
    const offset = Math.abs(targetTop - clientY) - thumbHeight / NUMBER_TWO;

    view.scrollTop = this.getScrollTopForOffset(offset);
  };

  handleHorizontalThumbMouseDown = event => {
    if (this.props.fullSize) return;

    const target = event.target;
    const clientX = event.clientX;
    const offsetWidth = target.offsetWidth;
    const left = target.getBoundingClientRect().left;
    this.handleDragStart(event);
    this.prevPageX = offsetWidth - (clientX - left);
  };

  handleVerticalThumbMouseDown = event => {
    if (this.props.fullSize) return;

    const target = event.target;
    const clientY = event.clientY;
    const offsetHeight = target.offsetHeight;
    const top = target.getBoundingClientRect().top;
    this.handleDragStart(event);
    this.prevPageY = offsetHeight - (clientY - top);
  };

  setupDragging() {
    document.addEventListener('mousemove', this.handleDrag);
    document.addEventListener('mouseup', this.handleDragEnd);
    document.onselectstart = () => false;
  }

  teardownDragging() {
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.handleDragEnd);
    document.onselectstart = undefined;
  }

  handleDragStart = event => {
    this.dragging = true;
    event.stopImmediatePropagation();
    this.setupDragging();
  };

  handleDrag = event => {
    const clientX = event.clientX;
    const clientY = event.clientY;
    const view = this.refs.view;
    let trackHorizontal;
    let thumbWidth;
    let trackVertical;
    let thumbHeight;
    let trackTop;
    let clickPosition;
    let offset;
    let trackLeft;
    if (this.prevPageX) {
      trackHorizontal = this.refs.trackHorizontal;
      trackLeft = trackHorizontal.getBoundingClientRect().left;
      thumbWidth = this.getThumbHorizontalWidth();
      clickPosition = thumbWidth - this.prevPageX;
      offset = -trackLeft + clientX - clickPosition;
      view.scrollLeft = this.getScrollLeftForOffset(offset);
    }
    if (this.prevPageY) {
      trackVertical = this.refs.trackVertical;
      trackTop = trackVertical.getBoundingClientRect().top;
      thumbHeight = this.getThumbVerticalHeight();
      clickPosition = thumbHeight - this.prevPageY;
      offset = -trackTop + clientY - clickPosition;
      view.scrollTop = this.getScrollTopForOffset(offset);
    }
    return false;
  };

  handleDragEnd = () => {
    this.dragging = false;
    this.prevPageX = this.prevPageY = 0;
    this.teardownDragging();
    this.handleDragEndAutoHide();
  };

  handleDragEndAutoHide() {
    this.props.autoHide && this.hideTracks();
  }

  handleTrackMouseEnter = event => {
    if (this.targetInChildScrollbox(event)) {
      this.handelViewMouseLeave();
      return;
    }

    if (!this.props.fullSize) {
      this.trackMouseOver = true;
      this.handleTrackMouseEnterAutoHide();
    }
  };

  handleTrackMouseEnterAutoHide() {
    this.props.autoHide && this.showTracks();
  }

  handleTrackMouseLeave = () => {
    if (!this.props.fullSize) {
      this.trackMouseOver = false;
      this.handleTrackMouseLeaveAutoHide();
    }
  };

  handleTrackMouseLeaveAutoHide() {
    this.props.autoHide && this.hideTracks();
  }

  showTracks() {
    const fullHeight = this.props.fullHeight;
    const fullWidth = this.props.fullWidth;

    this.hideTracksTimeout && clearTimeout(this.hideTracksTimeout);
    this.hideTracksTimeout = null;
    fastdom.mutate(() => {
      if (this.refs.trackHorizontal && !fullWidth) {
        this.refs.trackHorizontal.style.opacity = 1;
        this.refs.trackHorizontal.style.visibility = 'visible';
      }
      if (this.refs.trackVertical && !fullHeight) {
        this.refs.trackVertical.style.opacity = 1;
        this.refs.trackVertical.style.visibility = 'visible';
      }
    });
  }

  hideTracks() {
    const alwaysShowHorizontalScroll = this.props.alwaysShowHorizontalScroll;
    const alwaysShowVerticalScroll = this.props.alwaysShowVerticalScroll;
    if (this.dragging || this.scrolling || this.trackMouseOver) {
      return;
    }

    this.hideTracksTimeout && clearTimeout(this.hideTracksTimeout);
    this.hideTracksTimeout = setTimeout(() => {
      fastdom.mutate(() => {
        if (this.refs.trackHorizontal && this.refs.trackVertical) {
          // Never hide sticky horizontal scroll
          if (!alwaysShowHorizontalScroll) {
            this.refs.trackHorizontal.style.visibility = 'hidden';
            this.refs.trackHorizontal.style.opacity = 0;
          }
          if (!alwaysShowVerticalScroll) {
            this.refs.trackVertical.style.opacity = 0;
            this.refs.trackVertical.style.visibility = 'hidden';
          }
        }
      });
    }, this.props.autoHideTimeout);
  }

  detectScrolling() {
    const scrollbox = this;
    if (this.scrolling) {
      return;
    }
    this.scrolling = true;
    this.handleScrollStart();
    this.detectScrollingInterval = setInterval(() => {
      if (
        scrollbox.lastViewScrollLeft === scrollbox.viewScrollLeft &&
        scrollbox.lastViewScrollTop === scrollbox.viewScrollTop
      ) {
        !isNil(this.detectScrollingInterval) && clearInterval(this.detectScrollingInterval);
        scrollbox.scrolling = false;
        scrollbox.handleScrollStop();
      }
      scrollbox.lastViewScrollLeft = scrollbox.viewScrollLeft;
      scrollbox.lastViewScrollTop = scrollbox.viewScrollTop;
    }, NUMBER_DETECT_SCROLLING);
  }
  raf(callback) {
    if (this.requestFrame) {
      raf.cancel(this.requestFrame);
    }

    this.requestFrame = raf(() => {
      this.requestFrame = undefined;
      callback();
    });
  }

  update(callback) {
    if (this.props.fullSize) {
      return;
    }

    this.raf(() => {
      this._update(callback);
    });
  }

  _update(callback) {
    this.props.onUpdate && this.props.onUpdate();
    const autoHeight = this.props.autoHeight;
    const fullWidth = this.props.fullWidth;
    const fullHeight = this.props.fullHeight;
    let thumbHorizontal;
    let thumbVertical;
    let trackHorizontal;
    let trackVertical;
    let trackHorizontalWidth;
    let thumbHorizontalWidth;
    let thumbHorizontalX;
    let thumbHorizontalStyle;
    let trackVerticalHeight;
    let thumbVerticalHeight;
    let thumbVerticalY;
    let thumbVerticalStyle;
    let trackHorizontalClass;
    let trackVerticalClass;

    const values = this.getValues();
    if (values && getScrollbarWidth()) {
      thumbHorizontal = this.refs.thumbHorizontal;
      thumbVertical = this.refs.thumbVertical;
      trackHorizontal = this.refs.trackHorizontal;
      trackVertical = this.refs.trackVertical;

      // Use fastdom to async/batch measure DOM to avoid layout thrashing.
      fastdom.clear(this.fastdomMeasure);
      this.fastdomMeasure = fastdom.measure(() => {
        trackHorizontalWidth = getInnerWidth(trackHorizontal);
        thumbHorizontalWidth = this.getThumbHorizontalWidth();
        thumbHorizontalX =
          values.scrollLeft / (values.scrollWidth - values.clientWidth) * (trackHorizontalWidth - thumbHorizontalWidth);

        thumbHorizontalStyle = {
          width: `${thumbHorizontalWidth}px`,
          transform: `translateX(${thumbHorizontalX}px)`,
          msTransform: `translateX(${thumbHorizontalX}px)`,
          WebkitTransform: `translateX(${thumbHorizontalX}px)`
        };

        // const { scrollTop, clientHeight, scrollHeight } = values;
        trackVerticalHeight = getInnerHeight(trackVertical);
        thumbVerticalHeight = this.getThumbVerticalHeight();
        thumbVerticalY =
          values.scrollTop / (values.scrollHeight - values.clientHeight) * (trackVerticalHeight - thumbVerticalHeight);
        thumbVerticalStyle = {
          height: `${thumbVerticalHeight}px`,
          transform: `translateY(${thumbVerticalY}px)`,
          msTransform: `translateY(${thumbVerticalY}px)`,
          WebkitTransform: `translateY(${thumbVerticalY}px)`
        };
        trackHorizontalClass =
          values.scrollWidth > values.clientWidth && !fullWidth
            ? 'izenda-ScrollBox-Track_visible'
            : 'izenda-ScrollBox-Track_notVisible';
        trackVerticalClass =
          values.scrollHeight > values.clientHeight && !fullHeight
            ? 'izenda-ScrollBox-Track_visible'
            : 'izenda-ScrollBox-Track_notVisible';

        // Use fastdom to async/batch mutate DOM style to avoid layout thrashing.
        fastdom.clear(this.fastdomMutate);
        this.fastdomMutate = fastdom.mutate(() => {
          trackHorizontal.className = getTrackClasses(false, autoHeight, trackHorizontalClass);
          trackVertical.className = getTrackClasses(true, autoHeight, trackVerticalClass);

          style(thumbHorizontal, thumbHorizontalStyle);
          style(thumbVertical, thumbVerticalStyle);
        });
      });
    }

    if (typeof callback === 'function') {
      callback(values);
    }
  }

  render() {
    const scrollbarWidth = getScrollbarWidth();
    const autoHeight = this.props.autoHeight;
    const containerClass = this.props.className;
    const defaultContainerStyle = this.props.containerStyle;
    const autoHeightMin = this.props.autoHeightMin;
    const autoHeightMax = this.props.autoHeightMax;
    const fullSize = this.props.fullSize;
    const fullHeight = this.props.fullHeight;
    const shouldInjectScrollCallbackToChild = this.props.shouldInjectScrollCallbackToChild;

    const restProps = omit(
      [
        'fullSize',
        'onScroll',
        'onScrollStart',
        'onScrollStop',
        'autoHide',
        'autoHideTimeout',
        'autoHeight',
        'autoHeightMin',
        'autoHeightMax',
        'className',
        'children',
        'showWhenHover',
        'showTrackTimeout',
        'containerStyle',
        'isDisabled',
        'shouldInjectScrollCallbackToChild',
        'viewClassName',
        'fullHeight',
        'fullWidth',
        'stickyScrollBottom',
        'alwaysShowHorizontalScroll',
        'alwaysShowVerticalScroll',
        'onUpdate',
        'triggerOnScrollWhenScrollTo'
      ],
      this.props
    );

    // Scrollbox classes and style
    const containerClasses = classnames(containerClass, {
      'izenda-ScrollBox': !fullSize,
      'izenda-ScrollBox_fullSize': fullSize,
      u_autoHeight: !fullSize && !!autoHeight
    });

    const containerStyle = extend(
      !fullSize && autoHeight ? { minHeight: autoHeightMin, maxHeight: autoHeightMax } : {},
      defaultContainerStyle
    );

    const isScrollView = !fullSize && !autoHeight;

    // View classes and style
    const viewClasses = classnames(
      {
        'izenda-ScrollBox-View': isScrollView,
        'izenda-ScrollBox-View_fullSize': fullSize,
        'izenda-ScrollBox-View_autoHeight': !fullSize && autoHeight,
        'izenda-ScrollBox-View-overflowXY': isScrollView && !fullHeight,
        'izenda-ScrollBox-View-overflowX': isScrollView && fullHeight
      },
      this.props.viewClassName
    );

    const viewStyle = fullSize
      ? {}
      : extend(
          autoHeight
            ? {
                minHeight: autoHeightMin + scrollbarWidth,
                maxHeight: autoHeightMax + scrollbarWidth
              }
            : {},
          this.props.isDisabled
            ? { overflow: 'hidden' }
            : autoHeight
              ? {
                  // Hide scrollbars by setting a negative margin
                  marginRight: scrollbarWidth ? -scrollbarWidth : NUMBER_ZERO,
                  marginBottom: scrollbarWidth ? -scrollbarWidth : NUMBER_ZERO
                }
              : fullHeight
                ? { marginBottom: -scrollbarWidth }
                : {
                    // Hide scrollbars by setting a negative right bottom corner
                    top: 0,
                    left: 0,
                    bottom: -scrollbarWidth,
                    right: -scrollbarWidth,
                    position: 'absolute'
                  }
        );

    // Track classes and style
    const trackHorizontalClasses = getTrackClasses(false, autoHeight);
    const trackVerticalClasses = getTrackClasses(true, autoHeight);
    const trackStyle = !scrollbarWidth || this.props.fullSize ? { display: 'none' } : undefined;

    const getScrollTop = this.getScrollTop;
    const getScrollLeft = this.getScrollLeft;
    const getScrollWidth = this.getScrollWidth;
    const getScrollHeight = this.getScrollHeight;
    const scrollVertical = this.scrollVertical;
    const scrollHorizontal = this.scrollHorizontal;
    const scrollTo = this.scrollTo;

    const children = shouldInjectScrollCallbackToChild
      ? React.Children.map(
          this.props.children,
          child =>
            child
              ? React.cloneElement(child, {
                  getScrollTop,
                  getScrollLeft,
                  getScrollWidth,
                  getScrollHeight,
                  scrollVertical,
                  scrollHorizontal,
                  scrollTo
                })
              : null
        )
      : this.props.children;

    return (
      <div className={containerClasses} style={containerStyle} ref='container' {...restProps}>
        <div className={viewClasses} style={viewStyle} ref='view'>
          {children}
        </div>
        <div className={trackHorizontalClasses} style={trackStyle} ref='trackHorizontal'>
          <div className='izenda-ScrollBox-Thumb_horizontal' ref='thumbHorizontal' />
        </div>
        <div className={trackVerticalClasses} style={trackStyle} ref='trackVertical'>
          <div className='izenda-ScrollBox-Thumb_vertical' ref='thumbVertical' />
        </div>
      </div>
    );
  }
}

function getScrollbarWidth() {
  if (scrollbarWidth) {
    return scrollbarWidth;
  }
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    // Effect when resize event
    div.style.width = '100';
    div.style.height = '100';
    div.style.position = 'absolute';
    div.style.top = '-9999';
    div.style.overflow = 'scroll';
    div.style.border = 'none';
    div.style.overflowScrolling = 'touch';
    div.style.WebkitOverflowScrolling = 'touch';

    document.body.appendChild(div);
    scrollbarWidth = div.offsetWidth - div.clientWidth;
    document.body.removeChild(div);
  } else {
    scrollbarWidth = NUMBER_ZERO;
  }
  return scrollbarWidth;
}

function getInnerHeight(elem) {
  const computedStyle = window.getComputedStyle(elem);
  return elem.clientHeight - parseFloat(computedStyle.paddingTop) - parseFloat(computedStyle.paddingBottom);
}

function getInnerWidth(elem) {
  const computedStyle = window.getComputedStyle(elem);
  return elem.clientWidth - parseFloat(computedStyle.paddingLeft) - parseFloat(computedStyle.paddingRight);
}

function getTrackClasses(isVertical, autoHeight, extraClass) {
  return classnames(
    {
      'izenda-ScrollBox-Track_vertical': isVertical,
      'izenda-ScrollBox-Track_horizontal': !isVertical,
      'izenda-ScrollBox-Track_autoHeight': autoHeight
    },
    extraClass
  );
}

function tween(start, end, onUpdate) {
  const tween = new TWEEN.Tween(start)
    .to(end, NUMBER_TWEEN)
    .onUpdate(onUpdate);
  tween.start();
  animate();

  function animate(time) {
    raf(animate);
    TWEEN.update(time);
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.ScrollBox.js